import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, NumberInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Hammer, Plus, History, AlertTriangle, TrendingUp, TrendingDown, Minus, Trash2, Sparkles } from "lucide-react";
import { fmtINR, fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import TID from "@/constants/testIds";
import { ImportExportBar } from "@/components/foundation/ImportExportBar";

const TREND_META = {
    RISING: { tone: "red", Icon: TrendingUp, label: "Rising" },
    FALLING: { tone: "emerald", Icon: TrendingDown, label: "Falling" },
    STABLE: { tone: "slate", Icon: Minus, label: "Stable" },
};

const WINDOW_OPTIONS = [30, 90, 180, 365];

function RateIntelligencePanel() {
    const [windowDays, setWindowDays] = useState(90);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [explainBusy, setExplainBusy] = useState(false);

    const load = (days, explain) => {
        api.get("/steel-grades/rate-intelligence", { params: { window_days: days, explain } })
            .then((r) => setData(r.data))
            .catch((e) => setError(formatApiError(e)));
    };

    useEffect(() => { load(windowDays, false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [windowDays]);

    const askAI = async () => {
        setExplainBusy(true);
        try { await load(windowDays, true); } finally { setExplainBusy(false); }
    };

    if (error) return null; // reference-only widget — never blocks the main Steel Grades table
    const trends = data?.trends || [];
    const movers = trends.filter((t) => t.direction !== "STABLE");

    return (
        <Card className="p-5 mb-4" testId="rate-intelligence-panel">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="font-display text-[15px] font-semibold text-slate-900">Rate Intelligence</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                        % change per grade over the last {windowDays} days, computed from each grade&rsquo;s own recorded rate history &mdash; not a forecast.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Select
                        value={windowDays}
                        onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
                        className="h-8 text-[12px] w-auto"
                        data-testid="rate-intel-window-select"
                    >
                        {WINDOW_OPTIONS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
                    </Select>
                    <SlateButton
                        onClick={askAI}
                        disabled={explainBusy || movers.length === 0}
                        className="h-8 px-2.5 text-xs"
                        title={movers.length === 0 ? "No moving grades to explain" : "Ask the Intelligent Advisor to note what to watch on the biggest movers"}
                        data-testid="rate-intel-explain-btn"
                    >
                        <Sparkles className="h-3.5 w-3.5" /> {explainBusy ? "Thinking…" : "Explain top movers"}
                    </SlateButton>
                </div>
            </div>

            {!data ? (
                <div className="text-[12px] text-slate-400 py-4 text-center">Loading trends…</div>
            ) : trends.length === 0 ? (
                <div className="text-[12px] text-slate-400 py-4 text-center">No active steel grades yet.</div>
            ) : (
                <div className="divide-y divide-slate-100">
                    {trends.map((t) => {
                        const meta = TREND_META[t.direction] || TREND_META.STABLE;
                        const Icon = meta.Icon;
                        return (
                            <div key={t.grade_id} className="flex items-center justify-between py-2.5 gap-3" data-testid={`rate-intel-row-${t.grade_code}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono font-semibold text-[13px] text-slate-900">{t.grade_code}</span>
                                        <Pill tone={meta.tone}><Icon className="h-3 w-3 inline -mt-0.5 mr-0.5" />{meta.label}</Pill>
                                        {t.changes_in_window > 0 && (
                                            <span className="text-[11px] text-slate-400">{t.changes_in_window} change{t.changes_in_window === 1 ? "" : "s"} in window</span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">{t.grade_name}</div>
                                    {t.ai_note && <div className="text-[11px] text-[hsl(var(--copper))] mt-0.5 italic">{t.ai_note}</div>}
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-mono tabular-nums text-[14px] font-semibold text-slate-900">{fmtINR(t.current_rate)}/kg</div>
                                    <div className={`text-[11px] font-mono ${t.pct_change_window > 0 ? "text-red-600" : t.pct_change_window < 0 ? "text-emerald-600" : "text-slate-400"}`}>
                                        {t.pct_change_window > 0 ? "+" : ""}{t.pct_change_window}% vs {fmtINR(t.rate_window_start)}/kg
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}

export default function SteelGrades() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [rateOpen, setRateOpen] = useState(false);
    const [target, setTarget] = useState(null); // grade being rate-updated
    const [impact, setImpact] = useState(null);
    const [historyRows, setHistoryRows] = useState(null);
    const [createForm, setCreateForm] = useState({
        code: "", name: "", spec: "", rate_per_kg: 0,
        thickness_min_mm: 0, thickness_max_mm: 999,
        effective_date: new Date().toISOString().slice(0, 10),
        customer_tag: "", status: "APPROVED",
    });
    const [rateForm, setRateForm] = useState({ new_rate: 0, reason: "" });
    const [submitting, setSubmitting] = useState(false);
    const canEdit = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        api.get("/steel-grades").then((r) => setRows(r.data)).catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const openRateUpdate = async (g) => {
        setTarget(g);
        setRateForm({ new_rate: g.rate_per_kg, reason: "" });
        const { data } = await api.get(`/steel-grades/${g.id}/impact`);
        setImpact(data);
        const h = await api.get(`/steel-grades/${g.id}/history`);
        setHistoryRows(h.data);
        setRateOpen(true);
    };

    const submitRate = async () => {
        if (!target) return;
        setSubmitting(true);
        try {
            await api.post(`/steel-grades/${target.id}/update-rate`, {
                new_rate: parseFloat(rateForm.new_rate),
                reason: rateForm.reason,
                confirm_recalc_pending: (impact?.affected_zbcs ?? 0) > 0,
            });
            toast.success("Rate updated", {
                description: (impact?.affected_zbcs ?? 0) > 0
                    ? `${impact.affected_zbcs} ZBCs flagged 'Recalc Pending'`
                    : "Rate change recorded",
            });
            setRateOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/steel-grades", {
                ...createForm,
                rate_per_kg: parseFloat(createForm.rate_per_kg),
                thickness_min_mm: parseFloat(createForm.thickness_min_mm) || 0,
                thickness_max_mm: parseFloat(createForm.thickness_max_mm) || 999,
            });
            toast.success("Steel grade added");
            setCreateOpen(false);
            setCreateForm({
                code: "", name: "", spec: "", rate_per_kg: 0,
                thickness_min_mm: 0, thickness_max_mm: 999,
                effective_date: new Date().toISOString().slice(0, 10),
                customer_tag: "", status: "APPROVED",
            });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete steel grade ${row.code}? This action cannot be undone.`)) return;
        try {
            await api.delete(`/steel-grades/${row.id}`);
            toast.success("Steel grade deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid={TID.gradesPage}>
            <PageHeader
                eyebrow="FOUNDATION · LAYER 3"
                title="Steel Grades"
                subtitle="Single source of truth for all material rates. Changing a rate cascades a recalc-pending flag to every ZBC that uses this grade. No silent recalculations."
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ImportExportBar entity="steel-grades" label="Steel Grades" canImport={canEdit} onImported={load} />
                        {canEdit && (
                            <CopperButton onClick={() => setCreateOpen(true)} data-testid={TID.gradesAddBtn}>
                                <Plus className="h-4 w-4" /> Add Grade
                            </CopperButton>
                        )}
                    </div>
                }
            />

            <RateIntelligencePanel />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}

            {rows && rows.length === 0 && (
                <EmptyState icon={Hammer} title="No steel grades yet" description="Add the first grade to start costing parts." />
            )}

            {rows && rows.length > 0 && (
                <Card>
                    <div className="overflow-x-auto">
                        <table data-testid={TID.gradesTable} className="w-full table-rowhover">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/60">
                                    <th className="eyebrow text-left py-2 px-4">Code</th>
                                    <th className="eyebrow text-left py-2 px-4">Name</th>
                                    <th className="eyebrow text-left py-2 px-4">Specification</th>
                                    <th className="eyebrow text-right py-2 px-4">Thk (mm)</th>
                                    <th className="eyebrow text-right py-2 px-4">Rate (₹/kg)</th>
                                    <th className="eyebrow text-left py-2 px-4">Customer Tag</th>
                                    <th className="eyebrow text-left py-2 px-4">Effective</th>
                                    <th className="eyebrow text-left py-2 px-4">Status</th>
                                    <th className="eyebrow text-right py-2 px-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((g) => (
                                    <tr key={g.id} data-testid={TID.gradeRow(g.id)} className="border-b border-slate-100">
                                        <td className="py-1.5 px-4 text-[13px] font-mono font-semibold">{g.code}</td>
                                        <td className="py-1.5 px-4 text-[13px]">{g.name}</td>
                                        <td className="py-1.5 px-4 text-[12px] text-slate-500 max-w-xs truncate">{g.spec}</td>
                                        <td className="py-1.5 px-4 text-[12px] font-mono tabular-nums text-right text-slate-600">
                                            {(g.thickness_min_mm ?? 0)}–{(g.thickness_max_mm ?? 999)}
                                        </td>
                                        <td className="py-1.5 px-4 text-[13px] font-mono tabular-nums text-right font-semibold">
                                            {fmtINR(g.rate_per_kg)}
                                        </td>
                                        <td className="py-1.5 px-4 text-[12px] text-slate-600">{g.customer_tag || "—"}</td>
                                        <td className="py-1.5 px-4 text-[12px] text-slate-500">{g.effective_date ? g.effective_date.slice(0, 10) : fmtRelative(g.rate_updated_at)}</td>
                                        <td className="py-1.5 px-4">
                                            <Pill tone={(g.status === "APPROVED" || g.is_active) ? "emerald" : (g.status === "DRAFT" ? "amber" : "slate")}>
                                                {g.status || (g.is_active ? "Active" : "Inactive")}
                                            </Pill>
                                        </td>
                                        <td className="py-1.5 px-4 text-right">
                                            {canEdit && (
                                                <div className="inline-flex gap-1">
                                                    <SlateButton
                                                        onClick={() => openRateUpdate(g)}
                                                        className="h-7 px-2.5 text-xs"
                                                        data-testid={TID.gradeUpdateRateBtn(g.id)}
                                                    >
                                                        <TrendingUp className="h-3 w-3" /> Update Rate
                                                    </SlateButton>
                                                    <SlateButton
                                                        onClick={() => remove(g)}
                                                        variant="ghost"
                                                        className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                                        title={`Delete ${g.code}`}
                                                        data-testid={`grade-delete-${g.id}`}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </SlateButton>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Update rate modal — shows cascade impact */}
            <Modal
                open={rateOpen}
                onClose={() => setRateOpen(false)}
                title={target ? `Update Rate — ${target.code}` : ""}
                size="lg"
                footer={
                    <>
                        <SlateButton variant="outline" onClick={() => setRateOpen(false)}>Cancel</SlateButton>
                        <CopperButton onClick={submitRate} disabled={submitting || !rateForm.reason}>
                            {submitting ? "Saving…" : "Confirm Rate Change"}
                        </CopperButton>
                    </>
                }
            >
                {target && (
                    <div className="space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Current Rate">
                                <div className="h-9 px-3 rounded-md bg-slate-50 border border-slate-200 flex items-center font-mono tabular-nums text-sm">
                                    {fmtINR(target.rate_per_kg)} <span className="text-slate-400 ml-1">/kg</span>
                                </div>
                            </Field>
                            <Field label="New Rate (₹/kg)" required>
                                <NumberInput
                                    value={rateForm.new_rate}
                                    onChange={(e) => setRateForm({ ...rateForm, new_rate: e.target.value })}
                                    step="0.01"
                                    min="0"
                                    required
                                />
                            </Field>
                        </div>

                        <Field label="Reason for change" required helper="e.g., 'SAIL price circular dated 15-Mar-2026'">
                            <TextInput
                                value={rateForm.reason}
                                onChange={(e) => setRateForm({ ...rateForm, reason: e.target.value })}
                                placeholder="Supplier circular / market movement / index revision"
                                required
                            />
                        </Field>

                        {/* Cascade impact warning */}
                        {impact && (
                            <div className={`rounded-md border p-3 ${impact.affected_zbcs > 0 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
                                <div className="flex items-start gap-2.5">
                                    {impact.affected_zbcs > 0 ? (
                                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                    ) : (
                                        <Hammer className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                                    )}
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-slate-900">
                                            {impact.affected_zbcs > 0
                                                ? `${impact.affected_zbcs} ZBCs use this grade`
                                                : "No ZBCs currently use this grade"}
                                        </div>
                                        <div className="text-xs text-slate-600 mt-1">
                                            {impact.affected_zbcs > 0
                                                ? "All affected ZBCs will be flagged 'Rate Updated — Recalculation Pending'. They will NOT be recalculated automatically. Each must be opened and recalculated manually by a Corporate Admin."
                                                : "This rate change won't affect any existing costings."}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* History */}
                        {historyRows && historyRows.length > 0 && (
                            <div>
                                <div className="eyebrow text-slate-500 mb-2 flex items-center gap-1.5">
                                    <History className="h-3 w-3" /> RATE HISTORY · LAST {Math.min(historyRows.length, 5)}
                                </div>
                                <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
                                    {historyRows.slice(0, 5).map((h) => (
                                        <div key={h.id} className="px-3 py-2 flex items-center justify-between text-[12px]">
                                            <div>
                                                <span className="font-mono tabular-nums">{fmtINR(h.old_rate)} → {fmtINR(h.new_rate)}</span>
                                                <span className="text-slate-500 ml-2">· {h.reason}</span>
                                            </div>
                                            <div className="text-slate-400 text-[11px]">{h.changed_by_name} · {fmtRelative(h.changed_at)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Create grade modal */}
            <Modal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                title="Add Steel Grade"
                size="lg"
                footer={
                    <>
                        <SlateButton variant="outline" onClick={() => setCreateOpen(false)}>Cancel</SlateButton>
                        <CopperButton onClick={submitCreate} disabled={submitting}>
                            {submitting ? "Saving…" : "Create Grade"}
                        </CopperButton>
                    </>
                }
            >
                <form onSubmit={submitCreate} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Grade Code" required>
                            <TextInput value={createForm.code} onChange={(e) => setCreateForm({ ...createForm, code: e.target.value.toUpperCase() })} placeholder="IS2062-E300" required />
                        </Field>
                        <Field label="Rate (₹/kg)" required>
                            <NumberInput value={createForm.rate_per_kg} onChange={(e) => setCreateForm({ ...createForm, rate_per_kg: e.target.value })} step="0.01" required />
                        </Field>
                    </div>
                    <Field label="Grade Name" required>
                        <TextInput value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="IS 2062 E300 Grade" required />
                    </Field>
                    <Field label="Specification" required>
                        <TextInput value={createForm.spec} onChange={(e) => setCreateForm({ ...createForm, spec: e.target.value })} placeholder="Hot rolled, YS 300 MPa" required />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Thickness Min (mm)" helper="Lower bound for grade applicability">
                            <NumberInput value={createForm.thickness_min_mm} onChange={(e) => setCreateForm({ ...createForm, thickness_min_mm: e.target.value })} step="0.1" min="0" data-testid="grade-thickness-min" />
                        </Field>
                        <Field label="Thickness Max (mm)" helper="Upper bound; use 999 for any thickness">
                            <NumberInput value={createForm.thickness_max_mm} onChange={(e) => setCreateForm({ ...createForm, thickness_max_mm: e.target.value })} step="0.1" min="0" data-testid="grade-thickness-max" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <Field label="Effective Date" helper="Date this rate became valid">
                            <TextInput type="date" value={createForm.effective_date} onChange={(e) => setCreateForm({ ...createForm, effective_date: e.target.value })} data-testid="grade-effective-date" />
                        </Field>
                        <Field label="Customer Tag" helper="Blank = applies to all customers">
                            <TextInput value={createForm.customer_tag} onChange={(e) => setCreateForm({ ...createForm, customer_tag: e.target.value })} placeholder="e.g., Volvo CE India" data-testid="grade-customer-tag" />
                        </Field>
                        <Field label="Status">
                            <Select value={createForm.status} onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })} data-testid="grade-status">
                                <option value="APPROVED">Approved</option>
                                <option value="DRAFT">Draft</option>
                            </Select>
                        </Field>
                    </div>
                </form>
            </Modal>
        </div>
    );
}